import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useOnClickOutside } from 'usehooks-ts';

import { logEvent } from '~/config/firebase.config';
import { useAuth } from '~/services/auth';

import styles from './Navbar.redesign.module.css';

export type NavbarSpace = { id: string; name: string };

export type NavbarProps = {
    clientID: string;
    clientName?: string;
    /** All establishments the user can switch between (shown as a dropdown when > 1). */
    spaces?: NavbarSpace[];
    /** Currently selected establishment id. */
    currentSpace?: string;
    /** Called with the chosen establishment id. */
    onSwitchSpace?: (id: string) => void;
    /** Contenu facultatif au centre de la barre (annonce de saison) : ne prend que la place libre. */
    center?: React.ReactNode;
    /** Logo de l'établissement (formule Pro, `data:`) : remplace le picto Unboared. */
    brandLogo?: string | null;
};

function Svg(props: any) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            {...props}
        />
    );
}
function IconChevron({ className }: { className?: string }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="m6 9 6 6 6-6" />
        </svg>
    );
}
function IconGear() {
    return (
        <Svg>
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </Svg>
    );
}
function IconHelp() {
    return (
        <Svg>
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <path d="M12 17h.01" />
        </Svg>
    );
}
function IconUser() {
    return (
        <Svg>
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </Svg>
    );
}
function IconPower() {
    return (
        <Svg>
            <path d="M18.36 6.64A9 9 0 1 1 5.64 6.64M12 2v10" />
        </Svg>
    );
}

export function Navbar({ clientID, clientName, spaces, currentSpace, onSwitchSpace, center, brandLogo }: NavbarProps) {
    const { t } = useTranslation();
    const { logout } = useAuth();
    const navigate = useNavigate();

    const switcherRef = useRef(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    // Type-ahead buffer for the space switcher (reset after a short pause).
    const typeBuf = useRef('');
    const typeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useOnClickOutside(switcherRef, () => setSwitcherOpen(false));

    const isDemo = clientID === 'demo';
    const hasSwitcher = !!spaces && spaces.length > 1 && !!onSwitchSpace;

    // Focus the popover when it opens so it captures key presses, and start the
    // highlight on the current space.
    useEffect(() => {
        if (switcherOpen) popoverRef.current?.focus();
        else typeBuf.current = '';
    }, [switcherOpen]);

    // Keep the highlighted item scrolled into view.
    useEffect(() => {
        if (!switcherOpen) return;
        const el = popoverRef.current?.querySelector(`[data-idx="${activeIndex}"]`) as HTMLElement | null;
        el?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex, switcherOpen]);

    useEffect(() => () => clearTimeout(typeTimer.current ?? undefined), []);

    const handleAccount = () => {
        logEvent('home_account_click', isDemo ? 'Demo' : 'Home');
        navigate('/account', { state: { clientID } });
    };

    const handleLogout = () => {
        logEvent('home_logout_click', isDemo ? 'Demo' : 'Home');
        logout();
        if (isDemo) {
            localStorage.removeItem('clientID');
            navigate('/signin');
        }
    };

    const openDashboard = () => {
        logEvent('home_dashboard_click', isDemo ? 'Demo' : 'Home');
        window.open(isDemo ? 'https://stats.unboared.com/demo' : 'https://stats.unboared.com', '_blank');
    };

    const openGuide = () => {
        logEvent('home_guide_click', isDemo ? 'Demo' : 'Home');
        window.open('https://guide.unboared.com', '_blank');
    };

    const selectSpace = (id: string) => {
        setSwitcherOpen(false);
        triggerRef.current?.focus();
        if (id !== currentSpace) onSwitchSpace?.(id);
    };

    const toggleSwitcher = () => {
        if (switcherOpen) {
            setSwitcherOpen(false);
            return;
        }
        const idx = spaces ? spaces.findIndex((s) => s.id === currentSpace) : -1;
        setActiveIndex(idx >= 0 ? idx : 0);
        setSwitcherOpen(true);
    };

    // Keyboard navigation + type-ahead inside the open space switcher.
    const onPopoverKeyDown = (e: React.KeyboardEvent) => {
        if (!spaces || spaces.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => Math.min(spaces.length - 1, i + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(0, i - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const s = spaces[activeIndex];
            if (s) selectSpace(s.id);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setSwitcherOpen(false);
            triggerRef.current?.focus();
        } else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
            typeBuf.current += e.key.toLowerCase();
            const q = typeBuf.current;
            let idx = spaces.findIndex((s) => s.name.toLowerCase().startsWith(q));
            if (idx < 0) idx = spaces.findIndex((s) => s.name.toLowerCase().includes(q));
            if (idx >= 0) setActiveIndex(idx);
            clearTimeout(typeTimer.current ?? undefined);
            typeTimer.current = setTimeout(() => {
                typeBuf.current = '';
            }, 700);
        }
    };

    return (
        <header className={styles.bar}>
            <div className={styles.brandZone} ref={switcherRef}>
                {brandLogo ? (
                    <img className={styles.brandLogo} src={brandLogo} alt={clientName || ''} />
                ) : (
                    <img className={styles.picto} src="/assets/images/logo-unboared-sm-tr.png" alt="Unboared" />
                )}
                {hasSwitcher ? (
                    <button
                        type="button"
                        ref={triggerRef}
                        className={styles.switcher}
                        onClick={toggleSwitcher}
                        aria-haspopup="listbox"
                        aria-expanded={switcherOpen}
                        aria-label={t('home.mySpaces')}
                    >
                        <span className={styles.spaceName}>{clientName}</span>
                        <IconChevron className={`${styles.chev} ${switcherOpen ? styles.chevOpen : ''}`} />
                    </button>
                ) : (
                    <span className={styles.spaceName}>{clientName}</span>
                )}

                {switcherOpen && hasSwitcher && (
                    <div
                        className={styles.popover}
                        role="listbox"
                        tabIndex={-1}
                        ref={popoverRef}
                        onKeyDown={onPopoverKeyDown}
                        aria-label={t('home.mySpaces')}
                        aria-activedescendant={`space-opt-${activeIndex}`}
                    >
                        <div className={styles.popLabel}>{t('home.mySpaces')}</div>
                        {spaces!.map((s, index) => {
                            const active = s.id === currentSpace;
                            const highlighted = index === activeIndex;
                            return (
                                <button
                                    type="button"
                                    key={s.id}
                                    id={`space-opt-${index}`}
                                    data-idx={index}
                                    className={`${styles.spaceItem} ${active ? styles.spaceItemActive : ''} ${
                                        highlighted ? styles.spaceItemHighlight : ''
                                    }`}
                                    onClick={() => selectSpace(s.id)}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    role="option"
                                    aria-selected={active}
                                >
                                    <span className={styles.spaceDot} />
                                    <span className={styles.spaceItemText}>{s.name}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {center && <div className={styles.center}>{center}</div>}

            <div className={styles.actions}>
                {!isDemo && (
                    <button
                        type="button"
                        className={styles.ghostBtn}
                        onClick={openDashboard}
                        aria-label={t('home.dashboard')}
                    >
                        <IconGear />
                        <span className={styles.ghostLabel}>{t('home.dashboard')}</span>
                    </button>
                )}
                <button type="button" className={styles.ghostBtn} onClick={openGuide} aria-label={t('home.guide')}>
                    <IconHelp />
                    <span className={styles.ghostLabel}>{t('home.guide')}</span>
                </button>

                {/* Bouton écrit en toutes lettres (même sur téléphone) : derrière un avatar, beaucoup
                    de clients ne trouvaient pas Mon compte. La déconnexion est dans Mon compte ;
                    la démo n'a pas de Mon compte, elle garde la déconnexion ici. */}
                {isDemo ? (
                    <button
                        type="button"
                        className={styles.ghostBtn}
                        onClick={handleLogout}
                        aria-label={t('home.logout')}
                    >
                        <IconPower />
                        <span className={styles.ghostLabel}>{t('home.logout')}</span>
                    </button>
                ) : (
                    <button type="button" className={styles.accountBtn} onClick={handleAccount}>
                        <IconUser />
                        {t('home.account')}
                    </button>
                )}
            </div>
        </header>
    );
}
